/**
 * Pure helpers for the Performance KPIs catalog list (ER-12473, KPIs Catalog Rules 10-12).
 * They translate between the KPIsTable / filter-modal vocabulary and the v2 KPI API.
 */

export const KPI_LIST_PAGE_SIZE = 25;

/** Status chips KPIsTable shows by default; the server applies the same visibility when no status is sent. */
export const DEFAULT_STATUS_SLUGS = Object.freeze(["active", "draft"]);

const STATUS_SLUG_TO_API = Object.freeze({
  draft: "DRAFT",
  active: "ACTIVE",
  retired: "RETIRED",
  archived: "ARCHIVED",
});

const FORMAT_LABEL_TO_API = Object.freeze({
  Currency: "CURRENCY",
  Percent: "PERCENT",
  Number: "NUMBER",
});

const FAVORABILITY_LABEL_TO_API = Object.freeze({
  More: "MORE_IS_FAVORABLE",
  Less: "LESS_IS_FAVORABLE",
});

const NO_FILTER = "No Filter";
const PENDING_CHANGES_SLUG = "pending-changes";

export const KPI_GENERIC_ERROR = "Something went wrong. Please try again.";
export const KPI_DUPLICATE_IDENTITY_MESSAGE =
  "A KPI with this name already exists in this account.";

export const createDefaultKpiFilters = () => ({
  statusSlugs: [...DEFAULT_STATUS_SLUGS],
  pendingChanges: false,
  format: null,
  favorability: null,
  currencyCode: null,
});

/** Turn the items emitted by the shared filter modal into the catalog filter state. */
export function parseKpiFilterItems(items = []) {
  const filters = { ...createDefaultKpiFilters(), statusSlugs: [] };
  items.forEach((item) => {
    if (typeof item === "string") {
      applyCheckValue(filters, item);
    } else if (item?.labelType) {
      applyRadioValue(filters, item.labelType, item.value);
    } else if (item?.value) {
      applyCheckValue(filters, item.value);
    }
  });
  return filters;
}

function applyCheckValue(filters, value) {
  const slug = String(value).toLowerCase();
  if (slug === PENDING_CHANGES_SLUG) {
    filters.pendingChanges = true;
  } else if (STATUS_SLUG_TO_API[slug] && !filters.statusSlugs.includes(slug)) {
    filters.statusSlugs.push(slug);
  }
}

function applyRadioValue(filters, labelType, value) {
  if (!value || value === NO_FILTER) return;
  if (labelType === "Format") filters.format = value;
  else if (labelType === "Favorability") filters.favorability = value;
  else if (labelType === "Operating Currency") filters.currencyCode = value;
}

/** Drop the filter behind a removed chip (chips show a status slug or a radio label). */
export function removeKpiFilterChip(filters, chip) {
  const target = String(chip).toLowerCase();
  const matches = (value) => value !== null && String(value).toLowerCase() === target;
  return {
    statusSlugs: filters.statusSlugs.filter((slug) => slug !== target),
    pendingChanges: target === PENDING_CHANGES_SLUG ? false : filters.pendingChanges,
    format: matches(filters.format) ? null : filters.format,
    favorability: matches(filters.favorability) ? null : filters.favorability,
    currencyCode: matches(filters.currencyCode) ? null : filters.currencyCode,
  };
}

/** Values the filter modal should show as selected, so reopening it reflects the applied filters. */
export function kpiFiltersToSelection(filters) {
  return [
    ...filters.statusSlugs,
    ...(filters.pendingChanges ? [PENDING_CHANGES_SLUG] : []),
    ...[filters.format, filters.favorability, filters.currencyCode].filter(Boolean),
  ];
}

const isDefaultStatusSelection = (slugs) =>
  slugs.length === DEFAULT_STATUS_SLUGS.length &&
  DEFAULT_STATUS_SLUGS.every((slug) => slugs.includes(slug));

/** Build the list query. No status is sent for the default selection, so the server's Active + Draft rule applies. */
export function buildKpiListParams({ page = 1, search = "", filters, currencies = [] }) {
  const params = { page, page_size: KPI_LIST_PAGE_SIZE };
  const { statusSlugs, pendingChanges, format, favorability, currencyCode } = filters;
  if (statusSlugs.length > 0 && !isDefaultStatusSelection(statusSlugs)) {
    params.status = statusSlugs.map((slug) => STATUS_SLUG_TO_API[slug]);
  }
  if (pendingChanges) params.has_pending_changes = "true";
  if (FORMAT_LABEL_TO_API[format]) params.format = FORMAT_LABEL_TO_API[format];
  if (FAVORABILITY_LABEL_TO_API[favorability]) {
    params.favorability = FAVORABILITY_LABEL_TO_API[favorability];
  }
  const currency = currencies.find((item) => item.code === currencyCode);
  if (currency) params.operating_currency = currency.id;
  const term = String(search || "").trim();
  if (term) params.search = term;
  return params;
}

function unitFormatOf(kpi) {
  if (kpi.format === "Currency") return `Currency (${kpi.operating_currency?.code ?? ""})`;
  if (kpi.format === "Number") return `Number (${kpi.unit_of_measure ?? ""})`;
  return "Percent (%)";
}

/** Map an API list row to the shape KPIsTable renders. Stored "Published" is displayed as Active. */
export function mapKpiListRow(kpi) {
  const status = String(kpi.status || "Draft").toLowerCase();
  return {
    id: kpi.id,
    name: kpi.name,
    version: kpi.version_number ?? 1,
    unitFormat: unitFormatOf(kpi),
    precision: kpi.decimal_precision ?? 0,
    lessIsBetter: kpi.favorability === "LESS_IS_FAVORABLE",
    targetsType: kpi.default_target_type || "Fixed",
    actualCalculation: kpi.default_aggregation || "Last",
    status: status === "published" ? "active" : status,
    hasPendingChanges: kpi.has_pending_changes === true,
  };
}

/** Readable message from a failed KPI request: the server's own text first (Rule 10 duplicate name included). */
export function extractKpiErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data === "string") {
    const text = data.trim();
    return text && !text.startsWith("<") ? text : KPI_GENERIC_ERROR;
  }
  const message = firstMessage(data?.detail) || firstMessage(data);
  if (message) return message;
  return data?.code === "KPI_DUPLICATE_IDENTITY"
    ? KPI_DUPLICATE_IDENTITY_MESSAGE
    : KPI_GENERIC_ERROR;
}

function firstMessage(value) {
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    return value.map(firstMessage).find(Boolean) || "";
  }
  if (value && typeof value === "object") {
    return (
      Object.entries(value)
        .filter(([key]) => key !== "code")
        .map(([, entry]) => firstMessage(entry))
        .find(Boolean) || ""
    );
  }
  return "";
}
